import { useState, useCallback } from 'react';

import { downloadFile, downloadMultipleFiles } from '../utils/downloadUtils';

const initialState: DownloadState = {
  isDownloading: false,
  progress: 0,
  error: null,
  downloadedCount: 0,
};

const initialResourceState = {
  isDownloading: false,
  progress: 0,
  error: null,
  isCompleted: false,
};

export const useDownload = (): UseDownloadReturn => {
  const [downloadState, setDownloadState] = useState<DownloadState>(initialState);
  const [resourceDownloads, setResourceDownloads] = useState<ResourceDownloadState>({});

  const resetDownloadState = useCallback(() => {
    setDownloadState(initialState);
  }, []);

  const resetResourceDownload = useCallback((resourceId: string) => {
    setResourceDownloads(prev => {
      const { [resourceId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const getResourceDownloadState = useCallback((resourceId: string) => {
    return resourceDownloads[resourceId] || null;
  }, [resourceDownloads]);

  const downloadSingle = useCallback(async (url: string, filename?: string, resourceId?: string) => {
    try {
      // Update global state
      setDownloadState({
        isDownloading: true,
        progress: 0,
        error: null,
        downloadedCount: 0,
      });

      // Update resource-specific state if resourceId is provided
      if (resourceId) {
        setResourceDownloads(prev => ({
          ...prev,
          [resourceId]: {
            isDownloading: true,
            progress: 0,
            error: null,
            isCompleted: false,
          },
        }));
      }

      await downloadFile(url, {
        filename,
        showProgress: true,
        onProgress: (progress) => {
          setDownloadState(prev => ({ ...prev, progress }));
          if (resourceId) {
            setResourceDownloads(prev => ({
              ...prev,
              [resourceId]: {
                ...prev[resourceId],
                progress,
              },
            }));
          }
        },
        onComplete: () => {
          setDownloadState(prev => ({
            ...prev,
            isDownloading: false,
            progress: 100,
            downloadedCount: 1,
          }));
          if (resourceId) {
            setResourceDownloads(prev => ({
              ...prev,
              [resourceId]: {
                ...prev[resourceId],
                isDownloading: false,
                progress: 100,
                isCompleted: true,
              },
            }));
          }
        },
        onError: (error) => {
          setDownloadState(prev => ({
            ...prev,
            isDownloading: false,
            error: error.message,
          }));
          if (resourceId) {
            setResourceDownloads(prev => ({
              ...prev,
              [resourceId]: {
                ...prev[resourceId],
                isDownloading: false,
                error: error.message,
              },
            }));
          }
        },
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Download failed';
      setDownloadState(prev => ({
        ...prev,
        isDownloading: false,
        error: errorMessage,
      }));
      if (resourceId) {
        setResourceDownloads(prev => ({
          ...prev,
          [resourceId]: {
            ...prev[resourceId],
            isDownloading: false,
            error: errorMessage,
          },
        }));
      }
    }
  }, []);

  const downloadMultiple = useCallback(
    async (downloads: { url: string; filename?: string; resourceId?: string }[]) => {
      try {
        setDownloadState({
          isDownloading: true,
          progress: 0,
          error: null,
          downloadedCount: 0,
        });

        // Initialize all resource states
        downloads.forEach(({ resourceId }) => {
          if (resourceId) {
            setResourceDownloads(prev => ({
              ...prev,
              [resourceId]: initialResourceState,
            }));
          }
        });

        await downloadMultipleFiles(downloads, {
          showProgress: true,
          onProgress: (progress) => {
            setDownloadState(prev => ({ ...prev, progress }));
          },
          onComplete: () => {
            setDownloadState(prev => ({
              ...prev,
              isDownloading: false,
              progress: 100,
              downloadedCount: downloads.length,
            }));
            // Mark all resources as completed
            downloads.forEach(({ resourceId }) => {
              if (resourceId) {
                setResourceDownloads(prev => ({
                  ...prev,
                  [resourceId]: {
                    ...prev[resourceId],
                    isDownloading: false,
                    progress: 100,
                    isCompleted: true,
                  },
                }));
              }
            });
          },
          onError: (error) => {
            setDownloadState(prev => ({
              ...prev,
              isDownloading: false,
              error: error.message,
            }));
          },
        });
      } catch (error) {
        setDownloadState(prev => ({
          ...prev,
          isDownloading: false,
          error: error instanceof Error ? error.message : 'Multiple download failed',
        }));
      }
    },
    []
  );

  return {
    downloadState,
    resourceDownloads,
    downloadSingle,
    downloadMultiple,
    resetDownloadState,
    resetResourceDownload,
    getResourceDownloadState,
  };
};
export interface DownloadState {
  isDownloading: boolean;
  progress: number;
  error: string | null;
  downloadedCount: number;
}

export type ResourceDownloadState = Record<string, {
    isDownloading: boolean;
    progress: number;
    error: string | null;
    isCompleted: boolean;
  }>;

export interface UseDownloadReturn {
  downloadState: DownloadState; // Global state for bulk operations
  resourceDownloads: ResourceDownloadState; // Per-resource state
  downloadSingle: (url: string, filename?: string, resourceId?: string) => Promise<void>;
  downloadMultiple: (downloads: { url: string; filename?: string; resourceId?: string }[]) => Promise<void>;
  resetDownloadState: () => void;
  resetResourceDownload: (resourceId: string) => void;
  getResourceDownloadState: (resourceId: string) => ResourceDownloadState[string] | null;
}

